import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ENVIRONMENT, LOCAL_STORAGE, WINDOW } from '@libs/shared/injections/window';
import { EnvironmentType } from '@libs/models/environment.type';

@Injectable({ providedIn: 'root' })
export class ClarityHelper {
  private cookie = inject(CookieService);
  private window = inject(WINDOW);
  private env = inject<EnvironmentType>(ENVIRONMENT);
  private localStorage = inject<Storage>(LOCAL_STORAGE);
  private readonly dpdFlagKey = 'SHOULD_SEND_DPD_EVENT';

  public get isDpdEventAvailable(): boolean {
    return !!this.localStorage.getItem(this.dpdFlagKey);
  }

  public initDpdEvent(clientId: number, dpdTerm: number): void {
    this.callClarity('event', 'LoanOverdue');
    this.callClarity('identify', `${clientId}`);
    this.callClarity('dpd', `${dpdTerm}`);

    this.localStorage.removeItem(this.dpdFlagKey);
  }

  public initLoanEvent(isFirst: boolean, clientId: number): void {
    this.callClarity('event', isFirst ? 'FirstLoan' : 'RepeatLoan');
    this.callClarity('identify', `${clientId}`);
  }

  public initApplicationEvent(isFirst: boolean, clientId: number): void {
    this.callClarity('event', isFirst ? 'FirstApplication' : 'RepeatApplication');
    this.callClarity('identify', `${clientId}`);
  }

  public initLoginEvent(clientId: number): void {
    this.callClarity('event', 'LogIn');
    this.callClarity('identify', `${clientId}`);

    this.localStorage.setItem(this.dpdFlagKey, 'true');
  }

  public initClickSignUpButtonEvent(): void {
    this.callClarity('event', 'ClickSignUpButton');
  }

  public initClickLoginButtonEvent(): void {
    this.callClarity('event', 'ClickLoginButton');
  }

  public initFirstSessionEvent(): void {
    const key = this.env.gaCookieSessionKey;
    if (!key) {
      return;
    }

    const gaTagContent = this.cookie.get(key);

    const startTimestamp = gaTagContent.match(/s(\d+)/)[1];
    const currentTimestamp = gaTagContent.match(/\$t(\d+)/)[1];

    if (startTimestamp !== currentTimestamp) {
      return;
    }

    this.callClarity('event', 'FirstSession');
    this.callClarity('set', 'ga_client_id', this.cookie.get('_ga').match(/GA\d+\.\d+\.(\d+\.\d+)/)[1]);
  }

  private isClarityAvailable(): boolean {
    return typeof this.window['clarity'] === 'function';
  }

  private callClarity(method: string, ...args: any[]): void {
    if (this.isClarityAvailable()) {
      try {
        this.window['clarity'](method, ...args);
      } catch (e) {
        console.warn(`[Clarity] Failed to call method "${method}":`, e);
      }
    }
  }
}
